import { Injectable } from '@angular/core'
import { SharedModule } from '@tv3/shared/shared.module'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  ChannelNameTokens,
  ConfirmDialogService,
  isSomething,
  loadRentals,
  Rental,
  selectAllRentals,
  selectOnce,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { ConnectionAirbnbService } from '@tv3/store/connection/connection-airbnb.service'
import { catchError, filter, map, mapTo, switchMap, take, tap } from 'rxjs/operators'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { SelectItemDialogService } from '@tv3/shared/dialogs/select-item-dialog/select-item-dialog.service'
import { AirbnbUpdateBookingPrefDialogService } from '@tv3/containers/channels/channel-connect/dialogs/airbnb-update-booking-pref-dialog/airbnb-update-booking-pref-dialog.service'
import { AirbnbUpdatePriceSettingsDialogService } from '@tv3/containers/channels/channel-connect/dialogs/airbnb-update-price-settings-dialog/airbnb-update-price-settings-dialog.service'
import { LoadConnectionsByChannel, RefreshConnectionsForChannel } from '@tv3/store/connection/connection.actions'
import { EMPTY, forkJoin, of } from 'rxjs'
import { SubscriptionPermissionCheckerService } from '@tv3/services/subscription-permission-checker.service'
import { AirbnbTaxSettingsDialogService } from '@tv3/containers/channels/channel-connect/dialogs/airbnb-tax-settings-dialog/airbnb-tax-settings-dialog.service'
import { ChannelImportStatus, Connection, getRealChannelId } from '@tv3/store/connection/connection.model'
import * as lodash from 'lodash'
import { AirbnbChannelService, airbnbListingSyncTypes } from '@tokeet-frontend/channels'

@Injectable({
  providedIn: SharedModule,
})
export class AirbnbHelperService {
  constructor(
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private confirmDialog: ConfirmDialogService,
    private selectItemDialog: SelectItemDialogService,
    private airbnbUpdateBookingPrefDialog: AirbnbUpdateBookingPrefDialogService,
    private airbnbUpdatePriceSettingsDialog: AirbnbUpdatePriceSettingsDialogService,
    private airbnbTaxSettingsDialogService: AirbnbTaxSettingsDialogService,
    private subscriptionPermissionCheckerService: SubscriptionPermissionCheckerService,
    private connectionAirbnbService: ConnectionAirbnbService,
    private airbnbChannelService: AirbnbChannelService
  ) {}

  private isAirbnbSyncPaused(connection: Connection) {
    return !!connection?.syncPaused || connection?.airbnbListed === false || !!connection?.airbnbDeleted
  }

  private getAirbnbPausedMessage(connection: Connection) {
    if (connection?.airbnbDeleted || connection?.syncPausedReason === 'deleted_on_airbnb') {
      return $localize`This Airbnb listing was deleted. AdvanceCM has paused API sync for it.`
    }
    if (connection?.syncPausedReason === 'unlisted_on_airbnb') {
      return $localize`This Airbnb listing is unlisted. AdvanceCM has paused API sync for it.`
    }
    return $localize`This Airbnb listing is no longer returned as active by Airbnb. AdvanceCM has paused API sync for it.`
  }

  private warnAirbnbSyncPaused(connection: Connection) {
    if (!this.isAirbnbSyncPaused(connection)) return false
    this.toaster.warning(this.getAirbnbPausedMessage(connection))
    return true
  }

  private preImportRooms(newRentals: number) {
    return this.store.pipe(
      selectOnce(selectAllRentals),
      switchMap((rentals) =>
        this.subscriptionPermissionCheckerService.check({
          checkAddRentalPermission: true,
          rentals: rentals ? rentals.length : 0,
          newRentals,
        })
      ),
      filter((allowed) => allowed)
    )
  }

  importRooms(connections: Connection[]) {
    const importableConnections = connections.filter((connection) => !this.isAirbnbSyncPaused(connection))
    if (importableConnections.length < 1) {
      this.toaster.warning($localize`Please select at least one active Airbnb listing.`)
      return EMPTY
    }
    if (importableConnections.length !== connections.length) {
      this.toaster.warning($localize`Paused Airbnb listings were skipped because API sync is disabled for them.`)
    }

    // One bulk job per connected account, rather than one queue message per listing: the
    // import status the backend records is per job, so a single job carries a meaningful
    // total/imported for the whole batch and can be polled to its real outcome.
    const accounts = lodash.groupBy(importableConnections, (connection) => connection.channelId)

    return this.preImportRooms(importableConnections.length).pipe(
      switchMap(() =>
        this.confirmDialog.confirm({
          title: $localize`Import listings from channel?`,
          body:
            $localize`This will import your listing data from the selected channel into AdvanceCM. AdvanceCM will only import listings that are not already imported. A new rental will be created in AdvanceCM with the data.` +
            `<br><br>` +
            $localize`Are you sure you want to import?`,
        })
      ),
      // Acknowledge right away: the outcome poll below can run for minutes, and some
      // callers have no pending UI of their own, so silence would read as a dead button.
      tap(() => this.toaster.info($localize`Listing import started. This may take a few minutes.`)),
      switchMap(() =>
        forkJoin(
          lodash.map(accounts, (items) =>
            this.connectionAirbnbService
              .importListings(
                items[0].channelId,
                items[0].propertyId,
                items.map((item) => item.roomId)
              )
              .pipe(
                switchMap(() => this.connectionAirbnbService.waitForImportResult(items[0].channelId)),
                // One account failing to queue must not cancel the other accounts' polls;
                // fold it into a failed result and let combineImportResults report it.
                catchError(() => of({ status: 'failed', total: items.length, imported: 0 } as ChannelImportStatus))
              )
          )
        )
      ),
      map((results) => this.combineImportResults(results)),
      tap((status) => {
        this.store.dispatch(loadRentals({})) // refresh rentals
        this.notifyImportResult(status)
      }),
      catchError((error) => {
        this.store.dispatch(ActionFailed({ error }))
        return EMPTY
      }),
      take(1)
    )
  }

  // Folds the per-account job results into one outcome. Any failed job fails the batch; a
  // job with an unknowable outcome (null) makes the batch unknowable unless one failed.
  private combineImportResults(results: (ChannelImportStatus | null)[]): ChannelImportStatus | null {
    const failed = results.find((status) => status?.status === 'failed')
    const unknown = results.some((status) => !status)
    const total = lodash.sumBy(results, (status) => status?.total || 0)
    const imported = lodash.sumBy(results, (status) => status?.imported || 0)
    if (failed) return { ...failed, total, imported }
    if (unknown) return null
    return { status: 'done', total, imported }
  }

  private notifyImportResult(status: ChannelImportStatus | null) {
    if (status?.status === 'done') {
      this.toaster.success(
        status.total
          ? $localize`Imported ${status.imported || 0} of ${status.total} listings.`
          : $localize`Your listings were imported.`
      )
    } else if (status?.status === 'failed') {
      this.toaster.error(
        $localize`Some listings could not be imported. You can retry the import from the channel details page.`
      )
    } else {
      // The import runs on in the background; we could not observe it finish.
      this.toaster.info($localize`Your listings are being imported. Progress is shown on the channel details page.`)
    }
  }

  importRoom(channelName: ChannelNameTokens, connection: Connection) {
    if (this.warnAirbnbSyncPaused(connection)) return EMPTY

    return this.preImportRooms(1).pipe(
      switchMap(() =>
        this.confirmDialog.confirm({
          title: $localize`Import property from channel?`,
          body:
            $localize`This will import your property listing data from the selected channel into AdvanceCM. AdvanceCM will only import properties that are not already imported. A new rental will be created in AdvanceCM with the data.` +
            `<br><br>` +
            $localize`Are you sure you want to import?`,
        })
      ),
      switchMap(() =>
        this.connectionAirbnbService.importRoom(
          channelName,
          connection.channelId,
          connection.roomId,
          connection.propertyId
        )
      ),
      // The import is queued at this point; acknowledge before the potentially long wait
      // for its outcome.
      tap(() => this.toaster.info($localize`Listing import started. This may take a few minutes.`)),
      switchMap(() => this.connectionAirbnbService.waitForImportResult(connection.channelId)),
      tap((status) => {
        this.store.dispatch(loadRentals({})) // refresh rentals
        this.store.dispatch(
          LoadConnectionsByChannel({ channelName, channelId: getRealChannelId(connection.channelId) })
        )
        this.notifyImportResult(status)
      }),
      catchError((error) => {
        this.store.dispatch(ActionFailed({ error }))
        return EMPTY
      }),
      take(1)
    )
  }

  setVisibilityV2(connection: ConnectionView) {
    return this.connectionAirbnbService
      .getVisibilityStatusV2(connection.channelId, connection.propertyId, connection.roomId)
      .pipe(
        switchMap(({ visible }) =>
          this.confirmDialog
            .confirm({
              title: $localize`Listing Visibility`,
              body: visible
                ? $localize`This listing is currently visible. Would you like to hide it?`
                : $localize`This listing is currently not visible. Would you like to make it visible?`,
              cancelText: $localize`Cancel`,
              confirmText: visible ? $localize`Hide` : $localize`Set Visible`,
            })
            .pipe(mapTo(!visible))
        ),
        switchMap((visible) =>
          this.connectionAirbnbService
            .setVisibilityStatusV2(connection.channelId, connection.propertyId, connection.roomId, visible)
            .pipe(mapTo(visible))
        ),
        tap(() => this.toaster.success($localize`Property visibility updated successfully.`)),
        catchError((error) => {
          this.store.dispatch(ActionFailed({ error }))
          return EMPTY
        }),
        take(1)
      )
  }

  updateBookingPref(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    this.airbnbUpdateBookingPrefDialog.open(connection)
  }

  updatePriceSettingsV2(connection: Connection) {
    if (this.warnAirbnbSyncPaused(connection)) return EMPTY
    return this.airbnbUpdatePriceSettingsDialog.open(connection).afterClosed()
  }

  updateTaxSettings(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return EMPTY
    return this.airbnbTaxSettingsDialogService.open(connection).afterClosed()
  }

  setSyncCategoryV2(connection: Connection) {
    if (this.warnAirbnbSyncPaused(connection)) return EMPTY
    return this.selectItemDialog
      .open({
        title: $localize`Change Synchronization`,
        help: $localize`You may change the synchronization between AdvanceCM and Airbnb for this listing. By default AdvanceCM will sync only rates and
       availability with your Airbnb listing. Rates and availability synchronization allows you to continue managing your listing settings
       in Airbnb. You may also choose a "Full Sync" between Airbnb and AdvanceCM which will allow you to update your Airbnb listing content,
       like images, via AdvanceCM.`,
        list: airbnbListingSyncTypes,
        bindValue: 'value',
        bindLabel: 'name',
        placeholder: $localize`Synchronization category`,
      })
      .pipe(
        filter((res) => isSomething(res)),
        switchMap((category: string) =>
          this.airbnbChannelService
            .updateListingSyncCategory({
              category: category,
              propertyId: connection.propertyId,
              roomid: connection.roomId,
              channelId: connection.channelId,
              name: connection.name,
            })
            .pipe(
              catchError((error) => {
                this.store.dispatch(ActionFailed({ error }))
                return EMPTY
              })
            )
        ),
        take(1),
        tap(() => this.toaster.success($localize`Sync category updated successfully.`))
      )
  }

  syncImageV2(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return

    this.confirmDialog
      .confirm({
        title: $localize`Sync images with channel?`,
        body: $localize`This will push your AdvanceCM rental images to the channel listing. Images which have not already been uploaded will be added to your channel.
        <br/><br/> Do you want to proceed?`,
      })
      .pipe(
        switchMap(() => {
          return this.connectionAirbnbService.syncImagesV2(
            connection.channelId,
            // @ts-ignore
            connection.propertyId,
            connection.roomId
          )
        }),
        take(1)
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Images are being synced.`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  updateListingDetailsV2(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return

    this.confirmDialog
      .confirm({
        title: $localize`Update listing details?`,
        body: $localize`This will update your channel listing details with AdvanceCM rental data. <br/><br/> Do you want to proceed?`,
      })
      .pipe(
        switchMap(() => {
          return this.connectionAirbnbService.updateListingDetailsV2(
            connection.channelId,
            // @ts-ignore
            connection.propertyId,
            connection.roomId
          )
        }),
        take(1)
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Details updated successfully.`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  pushRoomsV2(connection: ConnectionView, rental: Rental) {
    if (this.warnAirbnbSyncPaused(connection)) return

    this.connectionAirbnbService
      .pushRoomsV2(connection.propertyId, connection.rentalId, rental.abb_listing_id)
      .subscribe(
        () => {
          this.toaster.success($localize`Rooms pushed successfully.`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  deleteListingV2(connection: ConnectionView) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete your listing?`,
        body: $localize`This will delete your listing on the channel. This action cannot be undone once confirmed and your listing will be permanently
        deleted. You will have to create a new listing if you want the rental to reappear on the channel.
        <br/><br/> Do you want to proceed?`,
      })
      .pipe(
        switchMap(() => {
          // @ts-ignore
          return this.connectionAirbnbService.deleteListingV2(
            connection.channelId,
            // @ts-ignore
            connection.propertyId,
            connection.roomId
          )
        }),
        take(1)
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Listing deleted from the channel.`)
          this.store.dispatch(
            RefreshConnectionsForChannel({
              channelName: ChannelNameTokens.AirBnBV2API,
              channelId: getRealChannelId(connection.channelId),
              silent: true,
            })
          )
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
